import { type ElementType, forwardRef } from "react"
import { twMerge } from "tailwind-merge"
import { Icon } from "../icon"
import { type IconProps } from "../icon/Icon"
import { Tooltip } from "../overlays/tooltip/Tooltip"
import { LinkButton, type LinkButtonProps } from "./LinkButton"

export type IconLinkButtonProps = Omit<LinkButtonProps, "children"> & {
  /** An element that comes from the `@phosphor-icons/react` package. */
  icon: ElementType
  /** Props to pass to the icon such as `weight` */
  iconProps?: Omit<IconProps, "as">
  /** Used as the aria-label and tooltip label */
  label: string
  /** Whether to hide the tooltip */
  INACCESSIBLE_disableTooltip?: boolean
}

/**
 * A link component styled as an [IconButton](./IconButton.tsx) component. Is
 * wrapped in a `Tooltip` so the user can discover what the button does when
 * hovering.
 *
 * ```tsx
 * import { Star } from "@phosphor-icons/react"
 * import { IconLinkButton } from "@tokenterminal/ui/button/IconLinkButton"
 *
 * <IconLinkButton icon={Star} label="Star this" href="..." />
 * ```
 */

export const IconLinkButton = forwardRef<
  HTMLAnchorElement,
  IconLinkButtonProps
>(function IconLinkButton(
  { className, icon, iconProps, label, INACCESSIBLE_disableTooltip, ...props },
  ref
) {
  const button = (
    <LinkButton
      ref={ref}
      className={twMerge("tt-icon-button", className)}
      aria-label={label}
      {...props}
    >
      <Icon
        as={icon}
        className="pointer-events-none"
        aria-hidden
        {...iconProps}
      />
    </LinkButton>
  )

  return (
    <Tooltip
      content={label}
      triggerProps={{
        isDisabled: INACCESSIBLE_disableTooltip,
      }}
    >
      <Tooltip.Trigger>{button}</Tooltip.Trigger>
    </Tooltip>
  )
})
